import { type ThemeMode, type ResolvedTheme } from "@/contexts/ThemeContext";

// ==================== LOCAL STORAGE ====================

const THEME_STORAGE_KEY = 'theme-preference';

/**
 * Сохранить предпочтение темы пользователя в localStorage
 */
export const saveThemePreference = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Failed to save theme preference:', error);
  }
};

/**
 * Загрузить предпочтение темы пользователя из localStorage
 * @returns ThemeMode или 'auto' по умолчанию
 */
export const getThemePreference = (): ThemeMode => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && ['light', 'dark', 'auto'].includes(saved)) {
      return saved as ThemeMode;
    }
  } catch (error) {
    console.warn('Failed to load theme preference:', error);
  }
  
  return 'auto'; // Значение по умолчанию
};

/**
 * Удалить сохраненное предпочтение темы
 */
export const clearThemePreference = (): void => {
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear theme preference:', error);
  }
};

// ==================== SYSTEM THEME DETECTION ====================

/**
 * Определить системную тему браузера
 * @returns true если системная тема темная
 */
export const detectSystemTheme = (): boolean => {
  if (typeof window === 'undefined') {
    return false; // SSR fallback
  }
  
  if (window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  
  return false; // Fallback для старых браузеров
};

/**
 * Создать слушатель изменений системной темы
 * @param callback Функция обратного вызова при изменении темы
 * @returns Функция для отписки от событий
 */
export const createSystemThemeListener = (
  callback: (isDark: boolean) => void
): (() => void) => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {}; // SSR или старый браузер
  }
  
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  
  const handleChange = (e: MediaQueryListEvent) => {
    callback(e.matches);
  };
  
  // Современный способ подписки
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }
  // Fallback для старых браузеров  
  else if (mediaQuery.addListener) {
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }
  
  return () => {};
};

// ==================== THEME GENERATION ====================

/**
 * Параметры CSS переменных для тем
 */
export interface ThemeCSSVariables {
  background: string;
  foreground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
}

/**
 * Генерация светлой темы (CSS переменные в формате HSL)
 */
export const generateLightTheme = (): ThemeCSSVariables => {
  return {
    background: '0 0% 100%',
    foreground: '222.2 84% 4.9%',
    primary: '222.2 47.4% 11.2%',
    primaryForeground: '210 40% 98%',
    secondary: '210 40% 96%',
    secondaryForeground: '222.2 84% 4.9%',
    muted: '210 40% 96%',
    mutedForeground: '215.4 16.3% 46.9%',
    accent: '210 40% 96%',
    accentForeground: '222.2 84% 4.9%',
    destructive: '0 84.2% 60.2%',
    destructiveForeground: '210 40% 98%',
    border: '214.3 31.8% 91.4%',
    input: '214.3 31.8% 91.4%',
    ring: '222.2 84% 4.9%',
    card: '0 0% 100%',
    cardForeground: '222.2 84% 4.9%',
    popover: '0 0% 100%',
    popoverForeground: '222.2 84% 4.9%',
  };
};

/**
 * Генерация темной темы (CSS переменные в формате HSL)
 */
export const generateDarkTheme = (): ThemeCSSVariables => {
  return {
    background: '222.2 84% 4.9%',
    foreground: '210 40% 98%',
    primary: '210 40% 98%',
    primaryForeground: '222.2 84% 4.9%',
    secondary: '217.2 32.6% 17.5%',
    secondaryForeground: '210 40% 98%',
    muted: '217.2 32.6% 17.5%',
    mutedForeground: '215 20.2% 65.1%',
    accent: '217.2 32.6% 17.5%',
    accentForeground: '210 40% 98%',
    destructive: '0 62.8% 30.6%',
    destructiveForeground: '210 40% 98%',
    border: '217.2 32.6% 17.5%',
    input: '217.2 32.6% 17.5%',
    ring: '212.7 26.8% 83.9%',
    card: '222.2 84% 4.9%',
    cardForeground: '210 40% 98%',
    popover: '222.2 84% 4.9%',
    popoverForeground: '210 40% 98%',
  };
};

// ==================== CSS APPLICATION ====================

/**
 * Применить CSS переменные темы к документу
 */
export const applyCSSTheme = (theme: ThemeCSSVariables): void => {
  const root = document.documentElement;
  
  Object.entries(theme).forEach(([key, value]) => {
    // Конвертируем camelCase в kebab-case для CSS переменных
    const cssVariableName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVariableName, value);
  });
};

/**
 * Применить предустановленную тему
 */
export const applyPresetTheme = (theme: ResolvedTheme): void => {
  const themeVariables = theme === 'dark' 
    ? generateDarkTheme() 
    : generateLightTheme();
    
  applyCSSTheme(themeVariables);
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Конвертировать HEX цвет в HSL формат для совместимости с CSS переменными
 */
export const convertHexToHSL = (hex: string): string => {
  // Убираем # если есть
  const cleanHex = hex.replace('#', '');
  
  if (cleanHex.length !== 6) {
    console.warn(`Invalid hex color: ${hex}`);
    return '0 0% 0%'; // Fallback
  }
  
  // Конвертируем в RGB
  const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
  const b = parseInt(cleanHex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: 
        h = (g - b) / d + (g < b ? 6 : 0); 
        break;
      case g: 
        h = (b - r) / d + 2; 
        break;
      case b: 
        h = (r - g) / d + 4; 
        break;
    }
    h /= 6;
  }

  // Конвертируем в нужный формат
  const hDeg = Math.round(h * 360);
  const sPercent = Math.round(s * 100);
  const lPercent = Math.round(l * 100);

  return `${hDeg} ${sPercent}% ${lPercent}%`;
};

/**
 * Проверить валидность режима темы
 */
export const isValidThemeMode = (value: unknown): value is ThemeMode => {
  return typeof value === 'string' && ['light', 'dark', 'auto'].includes(value);
};

/**
 * Получить контрастный цвет (светлый или темный) для заданного фона
 */
export const getContrastColor = (backgroundColor: string): 'light' | 'dark' => {
  // Простая эвристика на основе яркости
  // В реальном проекте можно использовать более сложные алгоритмы
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Формула для определения яркости
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  return brightness > 128 ? 'dark' : 'light';
};